/**
 * The forced self-reference encoding step (TASKS T55, invariant 5, RESEARCH §6).
 *
 * > On a second failure, force a self-reference encoding step: the learner writes one short sentence
 * > linking the item to his own life. Persist it and surface it on future failures.
 *
 * The evidence is that self-reference encoding beats plain translation repetition at immediate and
 * one-week delay (MODERATE confidence). The mechanism this is built on is that an item which has
 * been failed twice is not going to be fixed by being shown a third time — the learner has now had
 * two clean retrieval attempts fail, and repetition is the thing that demonstrably does not work.
 *
 * Three properties, and each is a decision rather than an implementation detail:
 *
 *  1. **Unscored.** No grade, no `Attempt`, nothing that touches an FSRS interval. The sentence is
 *     not an answer and must never be treated as one — there is no right version of it.
 *  2. **Forced, not offered.** The state machine has no edge into `encoding` except from `feedback`
 *     after a graded failure, and no way past it except writing something. A skippable step is one
 *     that is skipped at 3:40 a.m., which is exactly when the item is being failed.
 *  3. **Surfaced later.** What he wrote comes back on the *next* failure of that item, because the
 *     whole point of a personal association is that it is retrieved, not that it was composed once.
 *
 * Pure. No DOM, no clock, no store.
 */

import type { ItemRecord } from '../store/schema';

/** Failures at which the step is forced. Two, per invariant 5 — not one, and not three. */
export const ENCODING_AT_FAILURE = 2;

/**
 * Does this item need the encoding step right now?
 *
 * Only once: an item that already has a sentence has done this, and asking again on every later
 * failure would turn a considered association into a chore. The sentence is *shown* on those later
 * failures instead, which is `previousEncoding`.
 */
export function requiresEncoding(record: ItemRecord): boolean {
  return record.failureCount >= ENCODING_AT_FAILURE && !hasEncoding(record);
}

export function hasEncoding(record: ItemRecord): boolean {
  return (record.encodingSentence ?? '').trim().length > 0;
}

/** What he wrote about this item before, if anything. Surfaced on later failures. */
export function previousEncoding(record: ItemRecord): string | null {
  const sentence = (record.encodingSentence ?? '').trim();
  return sentence.length > 0 ? sentence : null;
}

/**
 * The shortest thing that counts as having done it.
 *
 * Deliberately low. This is not scored and there is no right version, so a length rule can only ever
 * be an obstacle; its whole job is to stop an empty box from counting as a written sentence. A
 * higher bar would be a quality judgement about a private note, made by a program that cannot read
 * it.
 */
export const MIN_ENCODING_CHARS = 3;

export function isUsableEncoding(text: string): boolean {
  return text.trim().length >= MIN_ENCODING_CHARS;
}

/** Trimmed, and collapsed to single spaces. Stored exactly as it will be shown back. */
export function normaliseEncoding(text: string): string {
  return text.trim().replace(/\s+/g, ' ');
}

export interface EncodingPrompt {
  instruction: string;
  /** The Spanish this is about. Shown, because the sentence is *about* it. */
  es: string;
  en: string;
  /** What he wrote about this item before. Null the first time, which is the usual case. */
  previous: string | null;
  fieldLabel: string;
  announce: string;
}

/**
 * Build the encoding card's content.
 *
 * The instruction names his own life explicitly — "you", "yours" — because a vague "write a note"
 * produces a second translation, and a second translation is the repetition this step exists to
 * replace. The Spanish is on screen while he writes: this is not a retrieval attempt and hiding it
 * would only make the association harder to form.
 */
export function encodingPrompt(record: ItemRecord): EncodingPrompt {
  return {
    instruction: 'Write one line linking this to your own life',
    es: record.es,
    en: record.en,
    previous: previousEncoding(record),
    fieldLabel: 'Your line',
    // No "you got this wrong twice". The card is a consequence, not a reprimand, and nothing in this
    // game turns a lapse into a loss.
    announce: `${record.es}. ${record.en}. Write one line linking this to your own life.`,
  };
}

/** The record as it should be stored once the line is written. Unscored: nothing else changes. */
export function withEncoding(record: ItemRecord, text: string): ItemRecord {
  return { ...record, encodingSentence: normaliseEncoding(text) };
}
